import { BN, web3 } from "@project-serum/anchor";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  Token,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import { AccountMeta, PublicKey, Transaction } from "@solana/web3.js";
import { TREASURY_INDEX } from "../../common/constants/common.constants";
import { IClubTreasury } from "../../common/interfaces/clubs.interface";
import { IFinancialOfferRecord } from "../../common/interfaces/treasury.interface";
import { getDecimalsPerMint, round } from "../../utilities/helpers";
import {
  getOrCreateTokenAccount,
  getPercentageRights,
  writeValueToNewInt32Buffer,
} from "../common/helpers/common.helpers";
import {
  financialOfferSeed,
  financialRecordSeed,
  governanceSeed,
  programFactory,
  RPC_CONNECTION,
  splGovernanceProgram,
  treasuryDataSeed,
  treasurySeed,
  unqClubMemberSeed,
  unqClubSeed,
} from "../common/utilities";
import { sendTransaction } from "../transactions/sendTransaction";

const clubProgram = programFactory();

export async function sellShares(
  realmName: string,
  wallet: AnchorWallet,
  amountOfRights: number,
  price: number,
  totalFundraised: number,
  clubTreasury: IClubTreasury,
  buyer?: PublicKey
) {
  try {
    const { clubData, memberData, treasury, treasuryData } =
      await getFinancialOfferPDAs(realmName, wallet);

    const [financialRecord] = await getFinancialRecordPDA(
      treasuryData,
      wallet.publicKey
    );

    let financialOfferCount;

    try {
      const financialRecordAccounts =
        await clubProgram.account.financialRecord.fetch(financialRecord);

      financialOfferCount = writeValueToNewInt32Buffer(
        financialRecordAccounts.sellOffersCount + 1
      );
    } catch (error) {
      financialOfferCount = writeValueToNewInt32Buffer(1);
    }

    const [financialOffer] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        treasuryData.toBuffer(),
        financialOfferSeed,
        wallet.publicKey.toBuffer(),
        financialOfferCount,
      ],
      clubProgram.programId
    );
    const remainingAcc: AccountMeta[] = [];

    if (clubTreasury?.denominatedTokenInfo?.mint) {
      remainingAcc.push({
        pubkey: clubTreasury?.denominatedTokenInfo?.mint,
        isSigner: false,
        isWritable: false,
      });
    }

    const startSellSharesIx = clubProgram.instruction.createFinancialOffer(
      new BN(
        amountOfRights * getDecimalsPerMint(clubTreasury.denominatedTokenInfo)
      ),
      new BN(price * getDecimalsPerMint(clubTreasury?.denominatedTokenInfo)),
      buyer ? new PublicKey(buyer) : null,
      {
        accounts: {
          clubData,
          memberData,
          payer: wallet.publicKey,
          financialOffer,
          financialRecord,
          treasuryData,
          treasury,
          systemProgram: web3.SystemProgram.programId,
        },
        remainingAccounts: remainingAcc,
      }
    );
    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;

    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });
    tx.add(startSellSharesIx);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function getShares(
  clubTreasury: IClubTreasury
): Promise<IFinancialOfferRecord[]> {
  const financialRecordOffersAccounts =
    await clubProgram.account.financialRecordOffer.all([
      {
        memcmp: {
          offset: 8,
          bytes: clubTreasury.treasury.toBase58(),
        },
      },
    ]);

  const financialOfferRecords: IFinancialOfferRecord[] = [];
  for (const offer of financialRecordOffersAccounts) {
    const availableOwnershipForTrade =
      (offer.account.amountOfRights.toNumber() /
        clubTreasury.totalFinancialPower /
        getDecimalsPerMint(clubTreasury?.denominatedTokenInfo)) *
      100;
    const totalPrice = round(
      offer.account.price.toNumber() /
        getDecimalsPerMint(clubTreasury?.denominatedTokenInfo),
      clubTreasury.denominatedTokenInfo?.decimals
    );

    financialOfferRecords.push({
      seller: offer.account.seller.toString(),
      offeredTo: offer.account.buyer?.toString(),
      availableOwnershipForTrade: round(availableOwnershipForTrade, 2),
      offeredOwnershipForTrade: getPercentageRights(
        offer.account.initialAmountOfRights.toNumber(),
        clubTreasury
      ),
      availableSharesForTrade:
        offer.account.amountOfRights.toNumber() /
        getDecimalsPerMint(clubTreasury.denominatedTokenInfo),
      offeredSharesForTrade:
        offer.account.initialAmountOfRights.toNumber() /
        getDecimalsPerMint(clubTreasury.denominatedTokenInfo),
      pricePerShare: round(
        totalPrice / availableOwnershipForTrade,
        clubTreasury.denominatedTokenInfo?.decimals
      ),
      totalPrice,
      financialRecordOfferAddress: offer.publicKey,
      wantedTokenMint: offer.account.wantedTokenMint,
    });
  }

  return financialOfferRecords;
}

export async function acceptFinancialOffer(
  realmName: string,
  wallet: AnchorWallet,
  seller: PublicKey,
  amountToBuy: number,
  clubTreasury: IClubTreasury,
  financialRecordOfferAddress: PublicKey,
  totalFundraised: number,
  wantedTokenMint: PublicKey | null
) {
  try {
    const remainingAcc: AccountMeta[] = [];
    const { clubData, memberData, treasury, treasuryData } =
      await getFinancialOfferPDAs(realmName, wallet);

    const [buyerFinancialRecord] = await getFinancialRecordPDA(
      treasuryData,
      wallet.publicKey
    );

    const [sellerFinancialRecord] = await getFinancialRecordPDA(
      treasuryData,
      seller
    );

    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });

    if (wantedTokenMint) {
      let buyerTokenInfo: PublicKey | undefined = undefined;
      //Check if user has ATA
      const buyerATA = await Token.getAssociatedTokenAddress(
        ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
        TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
        wantedTokenMint, // mint
        wallet.publicKey, // owner
        false
      );
      const buyerAtaInfo = await RPC_CONNECTION.getAccountInfo(buyerATA);
      if (buyerAtaInfo) {
        buyerTokenInfo = buyerATA;
      } else {
        const buyerTA = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
          wallet.publicKey,
          {
            mint: wantedTokenMint,
          }
        );
        if (buyerTA.value.length) {
          buyerTokenInfo = buyerTA.value[0].pubkey;
        } else {
          throw new Error("You do not have enough funds");
        }
      }
      const sellerTokenInfo =
        wantedTokenMint &&
        (await getOrCreateTokenAccount(wantedTokenMint, seller, wallet));

      remainingAcc.push({
        pubkey: buyerTokenInfo,
        isSigner: false,
        isWritable: true,
      });
      remainingAcc.push({
        pubkey: sellerTokenInfo!.destination,
        isSigner: false,
        isWritable: true,
      });
      remainingAcc.push({
        pubkey: TOKEN_PROGRAM_ID,
        isSigner: false,
        isWritable: false,
      });

      sellerTokenInfo.instruction && tx.add(sellerTokenInfo.instruction);
    }

    const startSellSharesIx = clubProgram.instruction.acceptFinancialOffer(
      new BN(
        amountToBuy * getDecimalsPerMint(clubTreasury?.denominatedTokenInfo)
      ),
      {
        accounts: {
          clubData,
          memberData,
          payer: wallet.publicKey,
          seller: seller,
          financialOffer: financialRecordOfferAddress,
          buyerFinancialRecord,
          sellerFinancialRecord,
          treasuryData,
          treasury,
          systemProgram: web3.SystemProgram.programId,
        },
        remainingAccounts: remainingAcc,
      }
    );

    tx.add(startSellSharesIx);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function cancelFinancialOffer(
  realmName: string,
  wallet: AnchorWallet,
  financialRecordOfferAddress: PublicKey
) {
  try {
    const { clubData, treasury, treasuryData } = await getFinancialOfferPDAs(
      realmName,
      wallet
    );

    const [financialRecord] = await getFinancialRecordPDA(
      treasuryData,
      wallet.publicKey
    );

    const startSellSharesIx = clubProgram.instruction.cancelFinancialOffer({
      accounts: {
        clubData,
        payer: wallet.publicKey,
        financialOffer: financialRecordOfferAddress,
        financialRecord,
        treasuryData,
        treasury,
        systemProgram: web3.SystemProgram.programId,
      },
    });
    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;

    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });
    tx.add(startSellSharesIx);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const getFinancialOfferPDAs = async (
  realmName: string,
  wallet: AnchorWallet
) => {
  const treasuryBuffer = Buffer.alloc(4);

  treasuryBuffer.writeInt32LE(TREASURY_INDEX + 1, 0);

  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );
  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );
  const [memberData] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      wallet.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [treasury] = await PublicKey.findProgramAddress(
    [unqClubSeed, clubData.toBuffer(), treasurySeed, treasuryBuffer],
    clubProgram.programId
  );
  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  return {
    clubData,
    memberData,
    treasury,
    treasuryData,
  };
};

export const getFinancialRecordPDA = async (treasuryData, address) => {
  return await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      treasuryData.toBuffer(),
      financialRecordSeed,
      address.toBuffer(),
    ],
    clubProgram.programId
  );
};
